import { useState } from 'react';
import { Modal, Input, Table, Button, Space, Tag, Alert, Popconfirm, Switch, message } from 'antd';
import { convertCentsIntoDollar } from '../../../../helpers/genericHelper';
import API from '../../../../services/API';

export default function MerchantListModal({ open, onClose, merchants, totalSpendLimit, minSpendLimit, userId, onAdded }) {
  const [filter, setFilter] = useState('');
  const [submittingKey, setSubmittingKey] = useState(null);
  const [showUnavailable, setShowUnavailable] = useState(false);

  // The backend refuses every add while the account's spend power is under its minimum.
  const belowMinimum = (totalSpendLimit || 0) < (minSpendLimit || 0);

  const handleClose = () => {
    setFilter('');
    setShowUnavailable(false);
    onClose();
  };

  const handleAdd = async (record) => {
    try {
      setSubmittingKey(record.id);
      const response = await API.postMerchantActivationAddNewMerchant({
        userId,
        merchantName: record.id,
        // Spend limits are account-level since AVADEV-4175; authorization never reads this amount.
        allocatedSpendLimit: totalSpendLimit || 0,
      });
      if (response.success) {
        message.success('Merchant added.');
        onAdded && onAdded();
      } else {
        message.error(response.message);
      }
    } catch (error) {
      message.error('Failed to add merchant.');
      console.error(error);
    } finally {
      setSubmittingKey(null);
    }
  };

  // A deleted merchant stays listed even if its group is disabled, as it was before disabled groups were included.
  const isUnavailable = (m) => m.enabled === false && !m.isDeleted;
  const unavailableCount = (merchants || []).filter(isUnavailable).length;

  const filtered = (merchants || []).filter((m) => {
    if (!showUnavailable && isUnavailable(m)) return false;
    const term = filter.trim().toLowerCase();
    if (!term) return true;
    return (
      (m.displayName || '').toLowerCase().includes(term) ||
      (m.id || m.merchantGroupName || '').toLowerCase().includes(term)
    );
  });

  const columns = [
    {
      title: 'Merchant',
      dataIndex: 'displayName',
      key: 'displayName',
      render: (value, record) => value || record.merchantGroupName,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        let status = 'Active';
        if (record.deletedByAva) status = 'Deleted by Ava';
        else if (record.isDeleted) status = 'Deleted';
        // Disabled groups are hidden from users in the app; support can still force-add them.
        if (record.enabled === false) {
          return <Space>{status !== 'Active' && status}<Tag color="#8c8c8c">Hidden in App</Tag></Space>;
        }
        return status;
      },
    },
    {
      title: '',
      key: 'action',
      render: (_, record) => (
        <Popconfirm
          title={`Add ${record.displayName || record.merchantGroupName}?`}
          okText="Add"
          onConfirm={() => handleAdd(record)}
          disabled={belowMinimum}
        >
          <Button type="link" disabled={belowMinimum} loading={submittingKey === record.id}>
            Add
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Modal
      title="Merchants"
      open={open}
      onCancel={handleClose}
      footer={null}
      width={720}
    >
      {belowMinimum && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Account spend power (${convertCentsIntoDollar(totalSpendLimit || 0)}) is below the ${convertCentsIntoDollar(minSpendLimit)} minimum, so merchants can't be added.`}
        />
      )}
      <Input
        placeholder="Filter by merchant name"
        allowClear
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        style={{ marginBottom: 12 }}
      />
      {unavailableCount > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <Space>
            {`Show merchants unavailable in app (${unavailableCount})`}
            <Switch size="small" checked={showUnavailable} onChange={setShowUnavailable} />
          </Space>
        </div>
      )}
      <Table
        // Allowed merchants carry `id`; only the deleted ones merged in also have `merchantGroupName`.
        rowKey={(record) => record.id || record.merchantGroupName}
        dataSource={filtered}
        columns={columns}
        pagination={{ pageSize: 10, showSizeChanger: false }}
        size="small"
      />
    </Modal>
  );
}
